// src/pages/Admin/Adherents/EditAdherentAdminModal.jsx

import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import {
  FaSave,
  FaTimes,
  FaCloudUploadAlt,
  FaLink,
  FaImage,
  FaCrop,
  FaUser,
  FaIdCard,
  FaPhone,
  FaMapMarkerAlt,
  FaInfoCircle,
  FaCamera,
  FaExclamationTriangle,
  FaSpinner,
  FaCheckCircle,
} from "react-icons/fa";
import { updateAdherent } from "../../../services/adherentServices";
import { getSettings } from "../../../services/settingsServices";
import { toast } from "react-toastify";
import { useUser } from "./../../Home/UserContext";
import {
  uploadAdherentPhoto,
  toAbsolutePhotoUrl,
  cropAdherentPhoto,
} from "../../../services/adherentPhotoServices";
import CropPhotoModal from "./../../../components/CropPhotoModal/CropPhotoModal";
import { toMasterUrl } from "./../../../components/CropPhotoModal/CropPhotoModal";

const inputBaseClass =
  "min-h-[46px] w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:focus:border-blue-400 dark:focus:ring-blue-950/60";

const labelClass =
  "mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400";

const getPreviousSeason = (currentSeason) => {
  const [startYear, endYear] = (currentSeason || "").split("-").map(Number);
  if (!startYear || !endYear) return "";
  return `${startYear - 1}-${endYear - 1}`;
};

const getNextSeason = (currentSeason) => {
  const [startYear, endYear] = (currentSeason || "").split("-").map(Number);
  if (!startYear || !endYear) return "";
  return `${startYear + 1}-${endYear + 1}`;
};

const sanitizeDateForInput = (raw) => {
  if (!raw) return { value: "", invalid: false };

  const s = String(raw).trim();

  if (s === "0000-00-00") return { value: "", invalid: true };

  const d = new Date(s);

  if (Number.isNaN(d.getTime())) return { value: "", invalid: true };

  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");

  return { value: `${yyyy}-${mm}-${dd}`, invalid: false };
};

const isValidDateInput = (s) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;

  const [y, m, d] = s.split("-").map(Number);
  const dt = new Date(s);

  if (Number.isNaN(dt.getTime())) return false;

  return (
    dt.getFullYear() === y &&
    dt.getMonth() + 1 === m &&
    dt.getDate() === d
  );
};

const buildVersionedUrl = (url, version) => {
  if (!url) return "";
  if (url.startsWith("data:") || url.startsWith("blob:")) return url;

  return `${url}${url.includes("?") ? "&" : "?"}v=${version}`;
};

const SectionCard = ({ icon: Icon, title, description, children, className = "" }) => (
  <section
    className={`overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900/80 ${className}`}
  >
    <div className="border-b border-slate-100 bg-slate-50/80 px-4 py-3 dark:border-slate-800 dark:bg-slate-950/40">
      <div className="flex items-start gap-3">
        {Icon && (
          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-600 text-white shadow-sm">
            <Icon className="text-sm" />
          </div>
        )}

        <div>
          <h4 className="text-sm font-bold text-slate-900 dark:text-white">
            {title}
          </h4>
          {description && (
            <p className="mt-0.5 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
              {description}
            </p>
          )}
        </div>
      </div>
    </div>

    <div className="p-4">{children}</div>
  </section>
);

const Field = ({
  label,
  name,
  value,
  onChange,
  type = "text",
  required = false,
  placeholder = "",
  className = "",
  inputClassName = "",
  children,
  ...props
}) => (
  <div className={className}>
    <label className={labelClass}>{label}</label>

    {children || (
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        className={`${inputBaseClass} ${inputClassName}`}
        {...props}
      />
    )}
  </div>
);

const SelectField = ({
  label,
  name,
  value,
  onChange,
  children,
  className = "",
  disabled = false,
}) => (
  <div className={className}>
    <label className={labelClass}>{label}</label>
    <select
      name={name}
      value={value}
      onChange={onChange}
      disabled={disabled}
      className={`${inputBaseClass} ${
        disabled ? "cursor-not-allowed bg-slate-100 text-slate-500" : ""
      }`}
    >
      {children}
    </select>
  </div>
);

const ReadOnlyClubField = ({ value }) => (
  <div className="min-h-[46px] rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200">
    {value || "—"}
    <div className="mt-1 text-[11px] text-slate-500">
      Champ géré par le club.
    </div>
  </div>
);

const EditAdherentAdminModal = ({ isOpen, onClose, refreshList, adherent }) => {
  const [formData, setFormData] = useState({
    nom: "",
    prenom: "",
    email: "",
    email_secondaire: "",
    tel: "",
    tel_secondaire: "",
    representant_legal: "",
    adresse: "",
    cp: "",
    commune: "",
    genre: "",
    statut_inscription: "",
    date_naissance: "",
    lieu_naissance: "",
    photo_url: "",
    licence_number: "",
    poids: "",
    saison_active: "",
    notes_adherent: "",
    autorisation_photo: false,
    passsport_number: "",
    createdAt: "",
  });

  const { user } = useUser();
  const roleId = user?.role_id;
  const canEditClubFields = roleId === 1 || roleId === 3;

  const [seasons, setSeasons] = useState([]);
  const [imgOk, setImgOk] = useState(true);

  const [uploadMode, setUploadMode] = useState("upload");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [outputFormat, setOutputFormat] = useState("webp");
  const [selectedFileName, setSelectedFileName] = useState("");

  const fileInputRef = useRef(null);
  const [imgVersion, setImgVersion] = useState(0);

  const [isCropOpen, setIsCropOpen] = useState(false);
  const [invalidBirthDate, setInvalidBirthDate] = useState(false);

  const loadSeasons = useCallback(async () => {
    try {
      const settingsData = await getSettings();

      if (settingsData?.length) {
        const active = settingsData[0].saison_active;
        const prev = getPreviousSeason(active);
        const next = getNextSeason(active);

        setSeasons([prev, active, next].filter(Boolean));
      }
    } catch (e) {
      console.error("Erreur chargement settings:", e);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    loadSeasons();
  }, [isOpen, loadSeasons]);

  useEffect(() => {
    if (!adherent) return;

    const dn = sanitizeDateForInput(adherent.date_naissance);
    const ca = sanitizeDateForInput(adherent.createdAt);

    setInvalidBirthDate(dn.invalid);

    setFormData({
      nom: adherent.nom || "",
      prenom: adherent.prenom || "",
      email: adherent.email || "",
      email_secondaire: adherent.email_secondaire || "",
      tel: adherent.tel || "",
      tel_secondaire: adherent.tel_secondaire || "",
      representant_legal: adherent.representant_legal || "",
      adresse: adherent.adresse || "",
      cp: adherent.cp || "",
      commune: adherent.commune || "",
      genre: adherent.genre || "",
      statut_inscription: adherent.statut_inscription || "",
      date_naissance: dn.value,
      lieu_naissance: adherent.lieu_naissance || "",
      photo_url: adherent.photo_url || "",
      licence_number: adherent.licence_number || "",
      poids: adherent.poids || "",
      saison_active: adherent.saison_active || "",
      notes_adherent: adherent.notes_adherent || "",
      autorisation_photo: !!adherent.autorisation_photo,
      passsport_number: adherent.passsport_number || "",
      createdAt: ca.value,
    });

    setImgOk(true);
    setUploadMode(adherent.photo_url ? "upload" : "upload");
    setUploadProgress(0);
    setIsUploading(false);
    setSelectedFileName("");
    setImgVersion((v) => v + 1);
  }, [adherent]);

  const seasonOptions = useMemo(() => {
    return Array.from(
      new Set([formData.saison_active, ...seasons].filter(Boolean))
    );
  }, [formData.saison_active, seasons]);

  const absolutePhoto = useMemo(
    () => toAbsolutePhotoUrl(formData.photo_url || ""),
    [formData.photo_url]
  );

  const previewPhotoUrl = useMemo(
    () => buildVersionedUrl(absolutePhoto, imgVersion),
    [absolutePhoto, imgVersion]
  );

  const fullName = `${String(formData.nom || "").toUpperCase()} ${
    formData.prenom || ""
  }`.trim();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "date_naissance") {
      if (!value) {
        setInvalidBirthDate(false);
      } else {
        setInvalidBirthDate(!isValidDateInput(value));
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const openFilePicker = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    fileInputRef.current?.click();
  };

  const uploadSelectedFile = async (file) => {
    if (!file) return;

    if (!adherent?.id) {
      toast.error("ID adhérent manquant.");
      return;
    }

    if (!file.type?.startsWith("image/")) {
      toast.error("Merci de sélectionner une image.");
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(0);
      setSelectedFileName(file.name || "");

      const res = await uploadAdherentPhoto(adherent.id, file, {
        format: outputFormat,
        onUploadProgress: (pe) => {
          if (!pe.total) return;
          const percent = Math.round((pe.loaded * 100) / pe.total);
          setUploadProgress(percent);
        },
      });

      setFormData((prev) => ({
        ...prev,
        photo_url: res.photo_url || "",
      }));

      setImgOk(true);
      setImgVersion((v) => v + 1);

      toast.success("Photo uploadée et traitée en 400×400.");
    } catch (err) {
      console.error(err);
      toast.error(`Erreur upload : ${err?.message || "inconnue"}`);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleFileChange = async (e) => {
    e.stopPropagation();

    const file = e.target.files?.[0];

    if (!file) return;

    await uploadSelectedFile(file);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const file = e.dataTransfer.files?.[0];

    if (!file) return;

    await uploadSelectedFile(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (invalidBirthDate && formData.date_naissance) {
      toast.error(
        "La date de naissance est invalide. Corrige-la ou laisse le champ vide."
      );
      return;
    }

    try {
      if (isUploading) {
        toast.info("Patiente, l'upload est en cours…");
        return;
      }

      const payload = { ...formData };

      if (!canEditClubFields) {
        delete payload.statut_inscription;
        delete payload.saison_active;
      }

      await updateAdherent(adherent.id, payload);

      refreshList?.();
      onClose?.();

      toast.success("Adhérent mis à jour avec succès.");
    } catch (error) {
      console.error("Erreur lors de la mise à jour de l'adhérent", error);
      toast.error("Erreur lors de la mise à jour de l'adhérent.");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center p-0 sm:items-center sm:p-4">
      <div
        className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <div
        className="relative flex max-h-[94vh] w-full max-w-6xl flex-col overflow-hidden rounded-t-[2rem] bg-slate-50 shadow-2xl ring-1 ring-white/20 dark:bg-slate-950 sm:max-h-[90vh] sm:rounded-[2rem]"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-indigo-600 to-fuchsia-600 px-4 py-5 sm:px-6">
          <div className="absolute -right-16 -top-16 h-40 w-40 rounded-full bg-white/10 blur-2xl" />
          <div className="absolute -bottom-20 left-10 h-44 w-44 rounded-full bg-white/10 blur-2xl" />

          <div className="relative flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-white/70">
                Gestion adhérent
              </p>

              <h3 className="mt-1 truncate text-xl font-bold text-white sm:text-2xl">
                {fullName || "Modifier un adhérent"}
              </h3>

              <p className="mt-1 max-w-2xl text-sm text-white/80">
                Modifiez les informations principales, la photo, les coordonnées.
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-white ring-1 ring-white/20 transition hover:bg-white/25"
              title="Fermer"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6">
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-[340px_minmax(0,1fr)]">
              <aside className="space-y-5">
                <SectionCard
                  icon={FaCamera}
                  title="Photo de l’adhérent"
                  description="Ajoutez une photo depuis le téléphone ou collez une URL."
                >
                  <input
                    ref={fileInputRef}
                    id="adherent-photo-file"
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />

                  <div className="flex flex-col items-center">
                    <div className="relative">
                      <div
                        className="h-40 w-40 overflow-hidden rounded-[2rem] bg-slate-100 shadow-inner ring-4 ring-white dark:bg-slate-800 dark:ring-slate-900 sm:h-48 sm:w-48"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (uploadMode === "upload") openFilePicker(e);
                        }}
                      >
                        {formData.photo_url && imgOk ? (
                          <img
                            src={previewPhotoUrl}
                            alt="Aperçu adhérent"
                            onError={() => setImgOk(false)}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="flex h-full w-full flex-col items-center justify-center gap-2 px-4 text-center text-slate-400">
                            <FaImage className="text-3xl" />
                            <span className="text-xs font-semibold">
                              Aucune photo
                            </span>
                          </div>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={openFilePicker}
                        disabled={isUploading}
                        className="absolute -bottom-3 left-1/2 inline-flex min-h-[44px] -translate-x-1/2 items-center justify-center gap-2 whitespace-nowrap rounded-2xl bg-blue-600 px-4 py-2 text-sm font-bold text-white shadow-lg transition hover:bg-blue-700 disabled:opacity-60"
                      >
                        {isUploading ? (
                          <>
                            <FaSpinner className="animate-spin" />
                            Envoi
                          </>
                        ) : (
                          <>
                            <FaCloudUploadAlt />
                            Changer
                          </>
                        )}
                      </button>
                    </div>

                    {formData.photo_url && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsCropOpen(true);
                        }}
                        className="mt-8 inline-flex min-h-[46px] w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-100"
                      >
                        <FaCrop />
                        Recadrer / zoomer la photo
                      </button>
                    )}

                    {!imgOk && (
                      <div className="mt-4 flex w-full items-start gap-2 rounded-2xl bg-rose-50 px-3 py-2 text-xs text-rose-700 ring-1 ring-rose-100 dark:bg-rose-950/30 dark:text-rose-300 dark:ring-rose-900">
                        <FaExclamationTriangle className="mt-0.5 shrink-0" />
                        Impossible de charger l’image actuelle.
                      </div>
                    )}
                  </div>

                  <div className="mt-5 rounded-2xl bg-slate-50 p-2 ring-1 ring-slate-200 dark:bg-slate-950 dark:ring-slate-800">
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setUploadMode("upload")}
                        className={`inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm font-bold transition ${
                          uploadMode === "upload"
                            ? "bg-blue-600 text-white shadow-sm"
                            : "bg-white text-slate-600 hover:bg-slate-100 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
                        }`}
                      >
                        <FaCloudUploadAlt />
                        Upload
                      </button>

                      <button
                        type="button"
                        onClick={() => setUploadMode("url")}
                        className={`inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm font-bold transition ${
                          uploadMode === "url"
                            ? "bg-blue-600 text-white shadow-sm"
                            : "bg-white text-slate-600 hover:bg-slate-100 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
                        }`}
                      >
                        <FaLink />
                        URL
                      </button>
                    </div>
                  </div>

                  {uploadMode === "upload" ? (
                    <div className="mt-4 space-y-3">
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={openFilePicker}
                        onDrop={handleDrop}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        className="cursor-pointer rounded-2xl border-2 border-dashed border-blue-200 bg-blue-50/70 p-4 text-center transition hover:border-blue-400 hover:bg-blue-50 dark:border-blue-900 dark:bg-blue-950/30"
                      >
                        <FaCloudUploadAlt className="mx-auto text-3xl text-blue-600 dark:text-blue-300" />
                        <p className="mt-2 text-sm font-bold text-slate-800 dark:text-white">
                          Choisir une photo
                        </p>
                        <p className="mt-1 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                          Depuis le téléphone, la galerie ou l’appareil photo.
                        </p>

                        {selectedFileName && (
                          <div className="mt-3 rounded-xl bg-white px-3 py-2 text-xs font-semibold text-slate-600 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-300 dark:ring-slate-800">
                            {selectedFileName}
                          </div>
                        )}
                      </div>

                      <div>
                        <label className={labelClass}>Format de sortie</label>
                        <select
                          className={inputBaseClass}
                          value={outputFormat}
                          onChange={(e) => setOutputFormat(e.target.value)}
                          disabled={isUploading}
                        >
                          <option value="webp">WEBP recommandé</option>
                          <option value="jpg">JPG</option>
                          <option value="png">PNG</option>
                        </select>
                      </div>

                      {isUploading && (
                        <div className="rounded-2xl bg-white p-3 ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-800">
                          <div className="mb-2 flex items-center justify-between text-xs font-bold text-slate-500 dark:text-slate-400">
                            <span>Upload en cours</span>
                            <span>{uploadProgress}%</span>
                          </div>

                          <div className="h-3 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                            <div
                              className="h-full rounded-full bg-blue-600 transition-all"
                              style={{ width: `${uploadProgress}%` }}
                            />
                          </div>
                        </div>
                      )}

                      <div className="flex items-start gap-2 rounded-2xl bg-emerald-50 px-3 py-2 text-xs text-emerald-700 ring-1 ring-emerald-100 dark:bg-emerald-950/30 dark:text-emerald-300 dark:ring-emerald-900">
                        <FaCheckCircle className="mt-0.5 shrink-0" />
                        L’image sera traitée côté serveur en carré 400×400 et
                        optimisée.
                      </div>
                    </div>
                  ) : (
                    <div className="mt-4">
                      <label className={labelClass}>Lien de la photo</label>
                      <input
                        type="text"
                        name="photo_url"
                        value={formData.photo_url}
                        onChange={(e) => {
                          setImgOk(true);
                          handleChange(e);
                          setImgVersion((v) => v + 1);
                        }}
                        placeholder="https://… ou /uploads/…"
                        className={inputBaseClass}
                      />
                    </div>
                  )}
                </SectionCard>

                <SectionCard
                  icon={FaInfoCircle}
                  title="Autorisation"
                  description="Information utile pour les photos publiées."
                >
                  <label className="flex cursor-pointer items-center justify-between gap-4 rounded-2xl bg-slate-50 p-4 ring-1 ring-slate-200 dark:bg-slate-950 dark:ring-slate-800">
                    <span>
                      <span className="block text-sm font-bold text-slate-800 dark:text-white">
                        Autorisation photo
                      </span>
                      <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400">
                        L’adhérent autorise l’utilisation de son image.
                      </span>
                    </span>

                    <input
                      type="checkbox"
                      name="autorisation_photo"
                      checked={formData.autorisation_photo}
                      onChange={handleChange}
                      className="h-6 w-6 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                  </label>
                </SectionCard>
              </aside>

              <main className="space-y-5">
                <SectionCard
                  icon={FaUser}
                  title="État civil"
                  description="Identité, naissance, licence et informations sportives."
                >
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                    <Field
                      label="Nom"
                      name="nom"
                      value={formData.nom}
                      onChange={handleChange}
                      required
                    />

                    <Field
                      label="Prénom"
                      name="prenom"
                      value={formData.prenom}
                      onChange={handleChange}
                      required
                    />

                    <SelectField
                      label="Genre"
                      name="genre"
                      value={formData.genre}
                      onChange={handleChange}
                    >
                      <option value="">— Sélectionner —</option>
                      <option value="Masculin">Masculin</option>
                      <option value="Féminin">Féminin</option>
                    </SelectField>

                    <div>
                      <label className={labelClass}>Date de naissance</label>
                      <input
                        type="date"
                        name="date_naissance"
                        value={formData.date_naissance}
                        onChange={handleChange}
                        aria-invalid={invalidBirthDate ? "true" : "false"}
                        className={`${inputBaseClass} ${
                          invalidBirthDate
                            ? "!border-rose-500 !focus:border-rose-500 !focus:ring-rose-100"
                            : ""
                        }`}
                      />

                      {invalidBirthDate && (
                        <p className="mt-1.5 flex items-center gap-1 text-xs font-semibold text-rose-600">
                          <FaExclamationTriangle />
                          Date invalide à modifier.
                        </p>
                      )}
                    </div>

                    <Field
                      label="Lieu de naissance"
                      name="lieu_naissance"
                      value={formData.lieu_naissance}
                      onChange={handleChange}
                    />

                    <Field
                      label="Numéro de licence"
                      name="licence_number"
                      value={formData.licence_number}
                      onChange={handleChange}
                    />

                    <Field
                      label="Poids kg"
                      name="poids"
                      type="number"
                      step="0.1"
                      inputMode="decimal"
                      value={formData.poids}
                      onChange={handleChange}
                    />
                  </div>
                </SectionCard>

                <SectionCard
                  icon={FaPhone}
                  title="Coordonnées"
                  description="Contacts de l’adhérent ou de son représentant légal."
                >
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Field
                      label="Email"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                    />

                    <Field
                      label="Email secondaire"
                      type="email"
                      name="email_secondaire"
                      value={formData.email_secondaire}
                      onChange={handleChange}
                    />

                    <Field
                      label="Téléphone"
                      name="tel"
                      type="tel"
                      inputMode="tel"
                      value={formData.tel}
                      onChange={handleChange}
                    />

                    <Field
                      label="Téléphone secondaire"
                      name="tel_secondaire"
                      type="tel"
                      inputMode="tel"
                      value={formData.tel_secondaire}
                      onChange={handleChange}
                    />

                    <Field
                      label="Représentant légal"
                      name="representant_legal"
                      value={formData.representant_legal}
                      onChange={handleChange}
                      className="sm:col-span-2"
                    />
                  </div>
                </SectionCard>

                <SectionCard
                  icon={FaMapMarkerAlt}
                  title="Adresse"
                  description="Adresse postale de l’adhérent."
                >
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Field
                      label="Adresse"
                      name="adresse"
                      value={formData.adresse}
                      onChange={handleChange}
                      className="sm:col-span-2"
                    />

                    <Field
                      label="Code postal"
                      name="cp"
                      inputMode="numeric"
                      value={formData.cp}
                      onChange={handleChange}
                    />

                    <Field
                      label="Commune"
                      name="commune"
                      value={formData.commune}
                      onChange={handleChange}
                    />
                  </div>
                </SectionCard>

                <SectionCard
                  icon={FaIdCard}
                  title="Infos club"
                  description="Statut, saison, Pass’Sport et notes internes."
                >
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div>
                      <label className={labelClass}>Statut d’inscription</label>

                      {canEditClubFields ? (
                        <select
                          name="statut_inscription"
                          value={formData.statut_inscription}
                          onChange={handleChange}
                          className={inputBaseClass}
                        >
                          <option value="">— Sélectionner —</option>
                          <option value="Licencié">Licencié</option>
                          <option value="Inscrit">Inscrit</option>
                          <option value="Cours d'essai">Cours d'essai</option>
                          <option value="Licencié - Arrêt">
                            Licencié - Arrêt
                          </option>
                          <option value="Non Licencié - Arrêt">
                            Non Licencié - Arrêt
                          </option>
                          <option value="Licencié Hors Club">
                            Licencié Hors Club
                          </option>
                          <option value="Ancien Licencié">
                            Ancien Licencié
                          </option>
                          <option value="Ancien Licencié et Arrêt">
                            Ancien Licencié et Arrêt
                          </option>
                        </select>
                      ) : (
                        <ReadOnlyClubField value={formData.statut_inscription} />
                      )}
                    </div>

                    <div>
                      <label className={labelClass}>Saison active</label>

                      {canEditClubFields ? (
                        <select
                          name="saison_active"
                          value={formData.saison_active}
                          onChange={handleChange}
                          className={inputBaseClass}
                        >
                          {seasonOptions.length === 0 && (
                            <option value="">— Sélectionner —</option>
                          )}

                          {seasonOptions.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <ReadOnlyClubField value={formData.saison_active} />
                      )}
                    </div>

                    <Field
                      label="N° Pass’Sport"
                      name="passsport_number"
                      value={formData.passsport_number}
                      onChange={handleChange}
                    />

                    <Field
                      label="Date de création"
                      type="date"
                      name="createdAt"
                      value={formData.createdAt}
                      onChange={handleChange}
                    />

                    <div className="sm:col-span-2">
                      <label className={labelClass}>Notes</label>
                      <textarea
                        name="notes_adherent"
                        value={formData.notes_adherent}
                        onChange={handleChange}
                        rows={5}
                        className={`${inputBaseClass} resize-none leading-relaxed`}
                      />
                    </div>
                  </div>
                </SectionCard>
              </main>
            </div>
          </div>

          <div className="sticky bottom-0 z-10 border-t border-slate-200 bg-white/95 px-4 py-3 shadow-[0_-10px_30px_rgba(15,23,42,0.08)] backdrop-blur dark:border-slate-800 dark:bg-slate-950/95 sm:px-6">
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-2xl bg-slate-100 px-5 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
              >
                <FaTimes />
                Annuler
              </button>

              <button
                type="submit"
                disabled={isUploading}
                className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-2xl bg-blue-600 px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-blue-600/20 transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isUploading ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    Upload en cours
                  </>
                ) : (
                  <>
                    <FaSave />
                    Enregistrer les modifications
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>

      <CropPhotoModal
        isOpen={isCropOpen}
        onClose={() => setIsCropOpen(false)}
        entityId={adherent?.id}
        imageUrl={`${toAbsolutePhotoUrl(
          toMasterUrl(formData.photo_url)
        )}?v=${imgVersion}`}
        onCropped={(newUrl) => {
          setFormData((p) => ({ ...p, photo_url: newUrl }));
          setImgOk(true);
          setImgVersion((v) => v + 1);
        }}
        format="webp"
        cropAction={cropAdherentPhoto}
      />
    </div>
  );
};

export default EditAdherentAdminModal;